import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Cancel,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Download,
  Add,
} from '@mui/icons-material';
import {
  experimentService,
  type Experiment,
  type ExperimentAnalysis,
  type MetricAnalysis,
} from '../services/experimentService';
import { useNavigate } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const ExperimentResults: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [_selectedExperiment, setSelectedExperiment] = useState<number | null>(null);
  const [analysis, setAnalysis] = useState<ExperimentAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [experimentToCancel, setExperimentToCancel] = useState<number | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadExperiments();
  }, [activeTab]);

  const loadExperiments = async () => {
    try {
      setLoading(true);
      let data: Experiment[];
      
      if (activeTab === 0) {
        // Active experiments (status=RUNNING)
        data = await experimentService.getActiveExperiments();
      } else if (activeTab === 1) {
        // Completed experiments (status=COMPLETED)
        data = await experimentService.getCompletedExperiments();
      } else {
        // All experiments (no status filter)
        data = await experimentService.getExperiments();
      }
      
      setExperiments(data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load experiments');
      setExperiments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalysis = async (experimentId: number) => {
    try {
      setAnalysisLoading(true);
      const data = await experimentService.getExperimentAnalysis(experimentId);
      setAnalysis(data);
      setSelectedExperiment(experimentId);
      setDetailsOpen(true);
      setError(null);
    } catch (err) {
      setError('Failed to load analysis');
    } finally {
      setAnalysisLoading(false);
    }
  };

  const exportToCSV = (analysis: ExperimentAnalysis) => {
    const headers = ['Metric', 'Test Mean', 'Control Mean', 'Difference', '% Change', 'P-Value', 'Significant', 'Winner', 'Effect Size'];
    const rows = analysis.metrics.map(m => [
      m.metric,
      m.testMean.toFixed(2),
      m.controlMean.toFixed(2),
      m.difference.toFixed(2),
      m.percentageChange.toFixed(2),
      m.pValue.toFixed(4),
      m.significant ? 'Yes' : 'No',
      m.winner,
      m.effectSize.toFixed(3),
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `experiment-${analysis.experimentId}-results.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RUNNING':
        return 'primary';
      case 'COMPLETED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getWinnerColor = (winner: string) => {
    switch (winner) {
      case 'TEST':
        return 'success';
      case 'CONTROL':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getWinnerIcon = (winner: string) => {
    return winner === 'TEST' ? <CheckCircle /> : winner === 'CONTROL' ? <Cancel /> : undefined;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleCancelClick = (experimentId: number) => {
    setExperimentToCancel(experimentId);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (experimentToCancel === null) return;

    setCancelling(true);
    try {
      await experimentService.cancelExperiment(experimentToCancel, 'User requested cancellation');
      // Refresh the experiment list
      await loadExperiments();
      setCancelDialogOpen(false);
      setExperimentToCancel(null);
    } catch (err) {
      console.error('Failed to cancel experiment:', err);
      setError('Failed to cancel experiment. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const MetricResultCard: React.FC<{ metric: MetricAnalysis }> = ({ metric }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="h6" component="div">
            {metric.metric.toUpperCase()}
          </Typography>
          <Chip
            label={metric.winner}
            color={getWinnerColor(metric.winner)}
            icon={getWinnerIcon(metric.winner)}
            size="small"
          />
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">
              Test Group
            </Typography>
            <Typography variant="h6">
              {metric.metric === 'ctr'
                ? `${(metric.testMean * 100).toFixed(2)}%`
                : metric.testMean.toFixed(2)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">
              Control Group
            </Typography>
            <Typography variant="h6">
              {metric.metric === 'ctr'
                ? `${(metric.controlMean * 100).toFixed(2)}%`
                : metric.controlMean.toFixed(2)}
            </Typography>
          </Grid>
        </Grid>

        <Box mt={2}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2">Change:</Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              {metric.percentageChange >= 0 ? (
                <TrendingUp fontSize="small" color="success" />
              ) : (
                <TrendingDown fontSize="small" color="error" />
              )}
              <Typography
                variant="body2"
                color={metric.percentageChange >= 0 ? 'success.main' : 'error.main'}
              >
                {metric.percentageChange >= 0 ? '+' : ''}
                {metric.percentageChange.toFixed(2)}%
              </Typography>
            </Box>
          </Box>

          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2">P-Value:</Typography>
            <Typography variant="body2" fontWeight="bold">
              {metric.pValue.toFixed(4)}
              {metric.significant && ' ✓'}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2">Effect Size (Cohen's d):</Typography>
            <Typography variant="body2">{metric.effectSize.toFixed(3)}</Typography>
          </Box>

          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2">Confidence:</Typography>
            <Typography variant="body2">{(metric.confidence * 100).toFixed(1)}%</Typography>
          </Box>
        </Box>

        {metric.significant ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            Statistically significant result (p &lt; 0.05)
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            Not statistically significant - needs more data or longer duration
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  const navigate = useNavigate();

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Experiment Results
        </Typography>
        <Typography variant="body1" color="textSecondary">
          View and analyze your A/B testing experiments
        </Typography>
      </Box>

      <Box mb={3} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => navigate('/experiments/create')}
        >
          Create Experiment
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Active Experiments" />
          <Tab label="Completed" />
          <Tab label="All Experiments" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : !experiments || experiments.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="textSecondary">No active experiments</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {experiments.map((exp) => (
                    <TableRow key={exp.id}>
                      <TableCell>{exp.name}</TableCell>
                      <TableCell>
                        <Chip label={exp.status} color={getStatusColor(exp.status)} size="small" />
                      </TableCell>
                      <TableCell>{new Date(exp.startDate).toLocaleDateString()}</TableCell>
                      <TableCell>{new Date(exp.endDate).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            onClick={() => loadAnalysis(exp.id)}
                            disabled={analysisLoading}
                          >
                            View Analysis
                          </Button>
                          {(exp.status === 'DRAFT' || exp.status === 'RUNNING') && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleCancelClick(exp.id)}
                              disabled={cancelling}
                            >
                              Cancel
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : !experiments || experiments.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="textSecondary">No completed experiments</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {experiments.map((exp) => (
                    <TableRow key={exp.id}>
                      <TableCell>{exp.name}</TableCell>
                      <TableCell>
                        {exp.completedAt
                          ? new Date(exp.completedAt).toLocaleDateString()
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {Math.ceil(
                          (new Date(exp.endDate).getTime() -
                            new Date(exp.startDate).getTime()) /
                            (1000 * 60 * 60 * 24)
                        )}{' '}
                        days
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => loadAnalysis(exp.id)}
                          disabled={analysisLoading}
                        >
                          View Results
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {experiments.map((exp) => (
                    <TableRow key={exp.id}>
                      <TableCell>{exp.name}</TableCell>
                      <TableCell>
                        <Chip label={exp.status} color={getStatusColor(exp.status)} size="small" />
                      </TableCell>
                      <TableCell>{new Date(exp.startDate).toLocaleDateString()}</TableCell>
                      <TableCell>{new Date(exp.endDate).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            onClick={() => loadAnalysis(exp.id)}
                            disabled={exp.status !== 'COMPLETED' && exp.status !== 'RUNNING'}
                          >
                            View Details
                          </Button>
                          {(exp.status === 'DRAFT' || exp.status === 'RUNNING') && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleCancelClick(exp.id)}
                              disabled={cancelling}
                            >
                              Cancel
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>

      {/* Analysis Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {analysis && (
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{analysis.experimentName}</Typography>
              <Chip
                label={`Overall Winner: ${analysis.overallWinner}`}
                color={getWinnerColor(analysis.overallWinner)}
                icon={getWinnerIcon(analysis.overallWinner)}
              />
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {analysisLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : analysis ? (
            <Box>
              {/* Summary */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="textSecondary">
                      Duration
                    </Typography>
                    <Typography variant="h6">{analysis.summary.totalDays} days</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="textSecondary">
                      Test URLs
                    </Typography>
                    <Typography variant="h6">{analysis.summary.testUrlCount}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="textSecondary">
                      Control URLs
                    </Typography>
                    <Typography variant="h6">{analysis.summary.controlUrlCount}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="textSecondary">
                      Confidence
                    </Typography>
                    <Typography variant="h6">
                      {(analysis.summary.confidence * 100).toFixed(1)}%
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Metrics */}
              {analysis.metrics.map((metric) => (
                <MetricResultCard key={metric.metric} metric={metric} />
              ))}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {analysis && (
            <Button
              startIcon={<Download />}
              onClick={() => exportToCSV(analysis)}
              variant="outlined"
            >
              Export CSV
            </Button>
          )}
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => !cancelling && setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Experiment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this experiment? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
            No, Keep It
          </Button>
          <Button onClick={handleCancelConfirm} disabled={cancelling} color="error" variant="contained">
            {cancelling ? 'Cancelling...' : 'Yes, Cancel Experiment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExperimentResults;
